import type {
  Product,
  ProductAttributesOptionsColor
} from '#root/api/clients/product/data-contracts'
import type {
  AttributeSelection,
  OptionGroups,
  ProductAttributeType,
  ProductDetail,
  ProductDetailWithSections,
  UseFetchOptionsExt
} from '#types/product'

const FRANCHISE_OTHERS_GROUP = 'Others'

/**
 * Fetches and transforms product data for PDP display, including inventory, attributes, variants, and pricing.
 *
 * Handles:
 * - Product details and inventory fetching (lazy-loaded, client-only inventory)
 * - Variant filtering by color when `enableColorAsAttribute` is enabled
 * - Inventory-based availability for attributes and variants
 * - Digital/customs/ROBIS product detection (perpetual inventory)
 * - Benefit ratings grouping and sanitization
 * - HTML stripping from details and model measurements
 * - Price-based grouping and franchise color grouping from Product Details metadata
 * - Badge and eyebrow formatting with coming-soon date support
 *
 * @param id - Product ID (style or variant); reactively observed
 * @param options - Fetch options
 * @param options.selectedColorId - Product-specific selected color ID to append when color is an API attribute
 * @param options.enableColorAsAttribute - If true, filters variants by selected color and calculates color availability from inventory
 * @returns Object containing reactive product data and loading/error states
 * @returns inventoryPending - True while inventory request is pending
 * @returns inventoryError - Error object if inventory fetch fails
 * @returns inventory - Inventory data keyed by variant ID (client-only, lazy)
 * @returns productPending - True while product details request is pending
 * @returns productError - Error object if product fetch fails (triggers 404/500 error page unless `options.onResponseError` is provided)
 * @returns product - Computed transformed product object with:
 *   - `allVariantsOutOfStock`: boolean - True if all variants are out of stock
 *   - `attributes`: enriched with `isSingleton`, `available`, and API-driven `groupedOptions`
 *   - `badge`: formatted badge with coming-soon date support
 *   - `contentAttributes`: pass-through from API
 *   - `defaultColor`: value of default color option (used for style IDs)
 *   - `customsExternalUrl`: sanitized customs URL
 *   - `details`: sanitized product details with HTML stripped, benefit ratings grouped
 *   - `eyebrow`: formatted eyebrow with coming-soon date support
 *   - `id`: default color ID or product ID
 *   - `modelMeasurements`: HTML-stripped measurements
 *   - `price`: merged default color price + base price
 *   - `url`: the returned url with a fall back to the default color url
 *   - `variants`: enriched with `productInventoryState`, `stockStatusLabel`
 *
 * @example
 * ```vue
 * <script setup>
 * const { product, productPending, inventory } = await useProduct(
 *   productId,
 *   { enableColorAsAttribute: true }
 * )
 * </script>
 * ```
 *
 * @remarks
 * - Uses cached product value to prevent UI flicker during color/size switches
 * - Inventory fetch is client-only and lazy to avoid SSR overhead
 * - Product fetch triggers fatal error page on 404/500, unless the caller passes its own `onResponseError`
 *   (components rendering a product inline should do so, otherwise one failed fetch replaces the whole page)
 * - For digital products, size "X" is filtered out
 * - Benefit ratings are flattened and grouped into a single detail section
 * - HTML in details is stripped (with optional line break preservation via `allowDetailsLineBreaksOnPdp` flag)
 * - For ROBIS-only products, inventory is treated as perpetual (always available)
 * - When `enableColorAsAttribute` is true, variants are filtered by the returned product's selected color
 */
export async function useProduct(
  id: MaybeRefOrGetter<string>,
  options: UseFetchOptionsExt
) {
  const { products } = useApi()
  const { benefitRatingsMaxScale, disableGroupBy } = useAppConfig().pages.pdp
  const nuxtApp = useNuxtApp()
  const { $feature, $t } = nuxtApp
  const { selectedColorId, ...fetchOptions } = options

  const productId = toValue(id)

  const detailsId = computed(() => options.enableColorAsAttribute && toValue(selectedColorId)
    ? `${toValue(id)}${toValue(selectedColorId)}`
    : toValue(id))
  const isStyle = computed(() => PRODUCT_STYLE_ID_REGEX.test(detailsId.value))

  const { pending: inventoryPending, data: inventory, error: inventoryError } = products.inventory(
    id,
    {},
    {
      key: `productInventory-${productId}`,
      lazy: true,
      server: false
    }
  )

  const productDetailsKey = computed(() => `productDetails-${detailsId.value}`)
  const { data: fetchedProduct, pending: productPending, error: productError } = products.details(
    detailsId,
    {},
    {
      key: productDetailsKey,
      lazy: true,
      ...fetchOptions,
      onResponseError: (context) => {
        const handleError = unref(options?.onResponseError as any)

        if (handleError) return handleError(context)

        showError({ statusCode: context.response?.status >= 500 ? 500 : 404, fatal: true })
      },
    }
  )

  // Nuxt cached product
  const nuxtCachedProduct = computed<Product | null>(() =>
    (nuxtApp.payload.data[productDetailsKey.value]
      ?? nuxtApp.static?.data?.[productDetailsKey.value]
      ?? null) as Product | null
  )

  // ProductSource priority:
  // 1. Nuxt cached product
  // 2. Fetched product
  const productSource = computed<Product | null>(() =>
    nuxtCachedProduct.value
    ?? fetchedProduct.value
  )

  // Store the last valid product to avoid transient NULL states
  const lastProduct = ref<Product | null>(null)
  watch(productSource, (product) => {
    if (product) lastProduct.value = product
  }, { immediate: true })

  return {
    inventoryPending,
    inventoryError,
    inventory,
    productPending,
    productError,
    product: computed(() => {
      const productValue = productSource.value ?? lastProduct.value
      if (!productValue) return null

      const sanitizedDetails = (productValue.details || []).map((detail) => ({
        ...detail,
        data: detail.data.map((content) => {
          for (const key in content) {
            if (isString(content[key]) && content[key].match(HTML_REGEX)) {
              if ($feature.allowDetailsLineBreaksOnPdp) content[key] = content[key].replace(LINE_BREAK_REGEX, '\n')
              content[key] = content[key].replace(HTML_REGEX, '')
            }
          }

          return content
        })
      }))

      const sanitizedModelMeasurements = productValue.modelMeasurements?.map((modelMeasurement) =>
        modelMeasurement.match(HTML_REGEX) ? modelMeasurement.replace(HTML_REGEX, '') : modelMeasurement
      )

      const isBenefitRating = ({ id }) => id === 'benefitRatings'
      const isFeaturesExtended = ({ id }) => id === 'featuresExtended'
      const featuresExtendedDetails = sanitizedDetails.filter(isFeaturesExtended)
      const shouldGroupFeaturesExtended = featuresExtendedDetails.length > 1

      const groupedDetails: ProductDetailWithSections[] = sanitizedDetails.filter((item) =>
        !isBenefitRating(item) && (!shouldGroupFeaturesExtended || !isFeaturesExtended(item))
      ) as ProductDetailWithSections[]

      const benefitRatingIndex = sanitizedDetails.findIndex(isBenefitRating)
      const featuresExtendedIndex = sanitizedDetails.findIndex(isFeaturesExtended)

      if (benefitRatingIndex !== -1) {
        const groupedBenefitRatings = {
          expanded: sanitizedDetails[benefitRatingIndex].expanded,
          id: 'benefitRatings',
          layout: 'scale',
          label: $t.benefitRating,
          data: sanitizedDetails
            .filter(isBenefitRating)
            .flatMap(({ label, data }) => data.map(
              ({ maxValue, value, minLabel }) => ({
                maxValue: benefitRatingsMaxScale!.toString(),
                value: maxValue,
                minLabel: value,
                maxLabel: minLabel,
                label
              })
            ))
        } satisfies ProductDetail

        groupedDetails.splice(benefitRatingIndex, 0, groupedBenefitRatings)
      }

      if (shouldGroupFeaturesExtended) {
        const firstFeaturesExtendedDetail = featuresExtendedDetails[0]
        const orderedFeaturesExtendedDetails = [
          ...featuresExtendedDetails.filter(({ layout }) => layout === 'keyValue'),
          ...featuresExtendedDetails.filter(({ layout }) => layout !== 'keyValue')
        ]

        groupedDetails.splice(featuresExtendedIndex, 0, {
          ...firstFeaturesExtendedDetail,
          data: [],
          sections: orderedFeaturesExtendedDetails
        })
      }

      const defaultColor = getDefaultColorOption(productValue.attributes, productValue.id) || {}
      const isDigital = productValue.productType?.toLowerCase() === 'digital'
      const isRobisOnly = $feature.allowProductReservationOnPDP && productValue?.isRobisOnly
      const isPerpetual = isDigital || isCustomsProduct(productValue) || isRobisOnly

      let variants = isArray(productValue.variants) ? productValue?.variants : []

      if (options?.enableColorAsAttribute && isStyle.value && defaultColor.value)
        variants = variants.filter((variant) => variant.attributes.color === defaultColor.value)

      variants = variants.map((variant) => {
        const inventoryVariants = inventory.value?.variants
        const inventoryRecord = inventoryVariants?.[variant.id]
        const quantity = isPerpetual ? 1 : inventoryRecord?.quantity || 0
        const stockStatus = isPerpetual ? 'AVAILABLE' : inventoryRecord?.stockStatus || 'OUT OF STOCK'

        let productInventoryState = variant.productInventoryState

        if (isPerpetual) {
          productInventoryState = 'InStock'
        }
        else if (productInventoryState !== 'SoldOut' && inventoryVariants) {
          productInventoryState = quantity > 0 ? 'InStock' : 'OutOfStock'
        }

        return {
          ...variant,
          productInventoryState,
          stockStatusLabel: $t.stockStatus[stockStatus]
        }
      })

      // Check if there is any attribute option that needs to be marked as unavailable according to its inventory
      const attributes = productValue.attributes?.map((attribute) => {
        // TEMP: mitigation against API not returning type field in attribute
        if ('code' in attribute) {
          // @ts-expect-error incorrect contract type
          attribute.type = attribute.code
          delete attribute.code
        }

        const getColorAvailability = (option): boolean => {
          if (option.id === productValue?.id)
            return variants.some(({ productInventoryState }) => productInventoryState === 'InStock')

          if (options?.enableColorAsAttribute && inventory.value?.variants) {
            const colorInventory = Object.entries(inventory.value.variants)
              .filter(([key, _]) => key.includes(`:${option.value}:`))

            return colorInventory.some(([_, { quantity }]) => quantity > 0)
          }
          return option.available
        }
        const attributeOptions = attribute.options.filter((option) => !(isDigital && option.label === 'X')).map((option) => {
          // VariantIds are a list of all variant IDs
          const variantIds = variants
            .filter((variant) => variant.attributes[attribute.type] === option.value)
            .map((variant) => variant.id)
          return attribute.type === 'color'
            ? {
                ...option,
                ...(attribute.groupBy === 'productSubGroup' && !option.label && option.value?.startsWith(`${option.id} - `)
                  ? { label: option.value.slice(`${option.id} - `.length) }
                  : {}),
                available: getColorAvailability(option)
              }
            : {
                ...option,
                label: option.label,
                // Treat an unresolved inventory state (still loading) as available rather than out of stock,
                // consistent with getAttrOptionAvailable's default - avoids sizes flashing as OOS while the
                // client-only inventory fetch is still pending.
                available: variants.filter(({ id }) => variantIds?.includes(id))
                  .some(({ productInventoryState }) => !productInventoryState || productInventoryState === 'InStock')
              }
        })

        return ({
          ...attribute,
          isSingleton: attributeOptions.length === 1,
          options: attributeOptions
        })
      })

        .map((attribute) => {
          const isSamePrice = (price1, price2) => Object.keys(price1).filter((key) => typeof price1[key] === 'number'
            && typeof price2[key] === 'number').every((key) => price1[key] === price2[key])

          const groupOptions = (attribute) => {
            const groups: OptionGroups[] = []
            const franchiseGroupingEnabled = attribute.type === 'color'
              && productValue.enableFranchisePDP === true
              && attribute.groupBy === 'productSubGroup'

            if (franchiseGroupingEnabled) {
              const othersGroup: OptionGroups = {
                groupValue: FRANCHISE_OTHERS_GROUP,
                options: []
              }

              attribute.options.forEach((option) => {
                const productSubGroups = [...new Set<string>((isArray(option.productSubGroup)
                  ? option.productSubGroup
                  : [option.productSubGroup])
                  .filter(isString)
                  .map((group) => group.trim())
                  .filter(Boolean))]

                if (!productSubGroups.length) {
                  othersGroup.options.push(option)
                  return
                }

                productSubGroups.forEach((productSubGroup) => {
                  const group = groups.find(({ groupValue }) => groupValue === productSubGroup)

                  if (group)
                    group.options.push(option)
                  else
                    groups.push({ groupValue: productSubGroup, options: [option] })
                })
              })

              if (othersGroup.options.length)
                groups.push(othersGroup)
            }
            else if (attribute.type === 'color' && attribute.options.some((option) => option.outletProduct)) {
              const standardOptions = attribute.options.filter((option) => !option.outletProduct)
              const outletOptions = attribute.options.filter((option) => option.outletProduct)

              if (standardOptions.length) groups.push({ groupValue: false, options: standardOptions })
              if (outletOptions.length) groups.push({ groupValue: true, options: outletOptions })
            }
            else if (!disableGroupBy && attribute.groupBy === 'price') {
              const uniquePrices = [attribute.options[0].price].filter(Boolean)

              // find unique prices
              attribute.options.forEach((option) => {
                if (option.price && uniquePrices.every((price) => !isSamePrice(price, option.price)))
                  uniquePrices.push(option.price)
              })

              // sort prices from highest to lowest
              uniquePrices.sort((a, b) => a.lowCurrent < b.lowCurrent ? 1 : -1)

              // create an option group for each unique price
              uniquePrices.forEach((price) => {
                groups.push({
                  groupValue: price,
                  options: attribute.options.filter((option) => isSamePrice(price, option.price))
                })
              })
            }
            else {
              groups.push({ options: attribute.options })
            }
            return groups
          }

          return ({
            ...attribute,
            groupedOptions: groupOptions(attribute)
          })
        })

      return {
        ...productValue,
        allVariantsOutOfStock: variants.every(({ productInventoryState }) => productInventoryState !== 'InStock'),
        attributes,
        badge: formatBadgeOrEyebrow(defaultColor.badge
          || productValue.badge, productValue.targetPublishDate, $feature.configProductComingSoonDateFormat),
        contentAttributes: productValue.contentAttributes,
        defaultColor: defaultColor.value,
        customsExternalUrl: productValue.customsExternalUrl?.replace(/\/customizer\.(.*?)\.html/g, '/customizer/$1'),
        details: groupedDetails,
        eyebrow: formatBadgeOrEyebrow(defaultColor.eyebrow
          || productValue.eyebrow, productValue.targetPublishDate, $feature.configProductComingSoonDateFormat),
        id: defaultColor.id || productValue.id,
        modelMeasurements: sanitizedModelMeasurements,
        price: { ...productValue.price, ...defaultColor.price },
        url: productValue.url || defaultColor?.url,
        variants,
      }
    })
  }
}

/**
 * Extracts the selected color option from a product's attributes.
 *
 * Searches the product's `attributes` array for a `color` type attribute and returns
 * the option matching the product's current ID. Used to display the active color swatch
 * and name on PDP.
 *
 * @param product - Product object (reactive or plain)
 * @returns Computed color option object or null if no color attribute exists
 *
 * @example
 * ```vue
 * <script setup>
 * const { product } = await useProduct(...)
 * const color = useProductColor(product)
 * // color.value = { id, label, value, swatch, badge, eyebrow, price, available }
 * </script>
 * ```
 */
export function useProductColor(product: MaybeRefOrGetter<Product | null>) {
  return computed(() =>
    toValue(product)?.attributes.reduce((colorObject: ProductAttributesOptionsColor | null, attribute) =>
      attribute.type === 'color' && !colorObject
        ? attribute.options.find(({ id }) => id === toValue(product)?.id) as ProductAttributesOptionsColor
        : colorObject, null) || null)
}

/**
 * Generates human-readable labels for all selected product attributes (color, size, width, etc.).
 *
 * Maps the current `selection` object (e.g., `{ size: 'M', color: 'blue' }`) to display labels
 * by looking up option labels in the product's attributes. Special handling for color: uses
 * `useProductColor` to get the color label instead of matching by value.
 *
 * @param product - Product object (reactive or plain)
 * @param selection - Object mapping attribute types to selected values (e.g., `{ size: 'M', width: 'REGULAR' }`)
 * @returns Computed object mapping attribute types to `{ label: string, value: string }` (e.g., `{ size: { label: 'Size', value: 'Medium' } }`)
 *
 * @example
 * ```vue
 * <script setup>
 * const { product } = await useProduct(...)
 * const selection = ref({ size: 'M', color: 'blue' })
 * const labels = useAttributeSelectionLabels(product, selection)
 * // labels.value = { size: { label: 'Size', value: 'Medium' }, color: { label: 'Color', value: 'Navy Blue' } }
 * </script>
 * ```
 *
 * @remarks
 * - Color label is derived from `useProductColor` (based on product ID), not from `selection.color`
 * - Useful for displaying selected options in cart, quickshop, or attribute summaries
 */
export function useAttributeSelectionLabels(
  product: MaybeRefOrGetter<Product | null>,
  selection: MaybeRefOrGetter<AttributeSelection>
) {
  const productColor = useProductColor(product)
  return computed(() => (toValue(product)?.attributes || []).reduce((attributes, { type, options, label }) => ({
    ...attributes,
    [type]: {
      label,
      value: type === 'color' ? productColor.value?.label : options?.find(({ value }) => value === toValue(selection)[type])?.label
    }
  }), {} as Record<ProductAttributeType, { label: string, value: string }>))
}
